import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { DEFAULT_LANGUAGE_BY_LOCALE, LangEnum, LocaleEnum } from '@models/commons/locales.enum';
import { Job } from '@models/job/job';
import { REQUEST, RESPONSE } from '@nguniversal/express-engine/tokens';
import { Store } from '@ngxs/store';
import { GetJob } from '@store/job/jobs.actions';
import { JobsState } from '@store/job/jobs.state';
import { SessionState } from '@store/session/session.state';
import { buildPath, WINDOW } from '@wizbii/angular-utilities';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import url from 'url';

@Injectable()
export class JobGuard implements CanActivate {
  subDomainByLang: Record<LangEnum, string> = {
    [LangEnum.fr]: 'www',
    [LangEnum.en]: 'en',
    [LangEnum.es]: 'es',
    [LangEnum.it]: 'it',
  };

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    @Inject(WINDOW) private readonly window: any,
    @Optional() @Inject(RESPONSE) private readonly response: any,
    @Optional() @Inject(REQUEST) private readonly request: any,
    @Inject(PLATFORM_ID) private readonly platformId: Record<string, unknown>
  ) {}

  canActivate(next: ActivatedRouteSnapshot): Observable<boolean> {
    const { jobId } = next.params;

    return this.store.select(SessionState.isInitialized).pipe(
      filter((isInitialized) => isInitialized),
      take(1), // session initialised
      switchMap(() =>
        this.store.dispatch(new GetJob(jobId)).pipe(
          withLatestFrom(this.store.select(JobsState.job(jobId))), // job retrieved
          map(([, job]) => {
            if (environment.platform === 'local' && isPlatformBrowser(this.platformId)) {
              return true;
            }
            const lang = this.getLang(job.locale);
            // Redirect to canonical company URL
            if (lang !== environment.lang) {
              this.redirect301(job, lang);
              return false;
            }

            return true;
          }),
          catchError(this.handleError.bind(this))
        )
      )
    );
  }

  private handleError(err: HttpErrorResponse | Error) {
    // Forbid activation to unknown company
    if (err instanceof HttpErrorResponse && err.status === 404) {
      this.redirect404();
      return of(false);
    }

    return throwError(err);
  }

  private getLang(locale?: LocaleEnum): LangEnum {
    return !!locale && !!DEFAULT_LANGUAGE_BY_LOCALE[locale] ? DEFAULT_LANGUAGE_BY_LOCALE[locale] : LangEnum.en;
  }

  private redirect301(job: Job, lang: LangEnum) {
    const redirectUrl = `https://${this.subDomainByLang[lang]}.${environment.apiDomain}/${FeaturesRoutingEnum.Company}/${job.company.id}/job/${job.id}`;
    const search =
      !!this.request && isPlatformServer(this.platformId)
        ? (url.parse(this.request.url).query ?? '').length > 0
          ? `?${url.parse(this.request.url).query ?? ''}`
          : ''
        : this.window.location.search;
    const queryParamsStr = search.length > 0 ? `?${search}` : '';

    if (this.response) {
      this.response.redirect(301, `${redirectUrl}${queryParamsStr}`);
      this.response.end();
    }

    this.window.open(redirectUrl, '_self');
  }

  private redirect404() {
    // Navigate to 404 component (which already takes care of updating server response)
    this.router.navigate([buildPath(FeaturesRoutingEnum.NotFound)], { skipLocationChange: true });
  }
}
